import { toast } from "sonner";

// Configuração de ambiente
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Função para construir URLs de API
const buildApiUrl = (endpoint: string) => {
  // Remove duplicação de /api se necessário
  const cleanEndpoint = endpoint.replace(/^\/api\//, '/');
  return `${API_BASE_URL}${cleanEndpoint.startsWith('/') ? cleanEndpoint : '/' + cleanEndpoint}`;
};
// Função para fazer requisições à API
const apiRequest = async (endpoint: string, method: string, data?: any) => {
  const url = buildApiUrl(endpoint);
  const options = {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
    ...(data && { body: JSON.stringify(data) }),
  };

  try {
    const response = await fetch(url, options);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  } catch (error) {
    console.error('Erro na requisição API:', error);
    throw error;
  }
};

// Asaas API interfaces
export interface AsaasCustomer {
  name: string;
  email: string;
  cpfCnpj: string;
  phone?: string;
  postalCode?: string;
  address?: string;
  addressNumber?: string;
  complement?: string;
  province?: string; // bairro
  city?: string;
}

export interface AsaasPaymentRequest {
  customer: string; // Customer ID
  billingType: 'BOLETO' | 'CREDIT_CARD' | 'PIX';
  dueDate: string; // Format: YYYY-MM-DD
  value: number;
  description: string;
  externalReference?: string;
  creditCard?: {
    holderName: string;
    number: string;
    expiryMonth: string;
    expiryYear: string;
    ccv: string;
  };
  creditCardHolderInfo?: {
    name: string;
    email: string;
    cpfCnpj: string;
    postalCode: string;
    addressNumber: string;
    phone: string;
  };
}

export interface AsaasPaymentResponse {
  id: string;
  invoiceUrl: string;
  status: string;
  bankSlipUrl?: string;
  pixQrCodeUrl?: string;
  pixCopiaECola?: string;
}

export interface AsaasCheckoutRequest {
  billingTypes: Array<'BOLETO' | 'CREDIT_CARD' | 'PIX'>;
  chargeTypes: Array<'DETACHED' | 'RECURRENT'>;
  minutesToExpire?: number;
  callback?: {
    successUrl: string;
    cancelUrl?: string;
    expiredUrl?: string;
    autoRedirect?: boolean;
  };
  items: Array<{
    description: string;
    name: string;
    quantity: number;
    value: number;
    imageBase64?: string;
  }>;
  customerData?: {
    name: string;
    cpfCnpj: string;
    email: string;
    phone?: string;
    address?: string;
    addressNumber?: string;
    complement?: string;
    province?: string;
    postalCode?: string;
    city?: string;
  };
  subscription?: {
    cycle: 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'SEMIANNUALLY' | 'YEARLY';
    endDate?: string;
    nextDueDate?: string;
    discount?: any;
    fine?: any;
    interest?: any;
  };
  installment?: {
    maxInstallmentCount: number;
  };
  // Optional fields for direct checkout creation
  customer?: string;
  externalReference?: string;
  dueDateLimitDays?: number;
}

export interface AsaasCheckoutResponse {
  id: string;
  checkoutUrl: string;
  link?: string; // Campo original da API Asaas
  expirationDate: string;
  status: string;
}

// IMPORTANT: This is a front-end implementation using a backend proxy
// All API keys are stored securely in the backend
// The frontend only communicates with our backend proxy, never directly with Asaas API
// These variables are only used for direct API calls that don't go through our proxy
const ASAAS_API_URL = 'https://api-sandbox.asaas.com/v3'; // Only used for direct API calls
const ASAAS_API_KEY = ''; // Empty as we're using the backend proxy for all operations

export interface AsaasSubscriptionRequest {
  customer: string;
  billingType: 'CREDIT_CARD';
  value: number;
  cycle: 'WEEKLY' | 'MONTHLY' | 'YEARLY' | 'BIWEEKLY' | 'QUARTERLY' | 'SEMIANNUALLY';
  nextDueDate: string; // YYYY-MM-DD
  description: string;
  creditCard: {
    holderName: string;
    number: string;
    expiryMonth: string;
    expiryYear: string;
    ccv: string;
  };
  creditCardHolderInfo: {
    name: string;
    email: string;
    cpfCnpj: string;
    postalCode: string;
    addressNumber: string;
    addressComplement?: string;
    phone?: string;
    mobilePhone?: string;
  };
}

/**
 * Handle API response and provide consistent error handling
 * @param response The fetch response object
 * @returns Parsed JSON response
 * @throws Error with error message from API or default message
 */
async function handleResponse(response: Response) {
  if (!response.ok) {
    let errorData;
    try {
      const text = await response.text();
      errorData = text ? JSON.parse(text) : { message: 'Erro ao processar requisição' };
    } catch {
      errorData = { message: 'Erro ao processar requisição' };
    }
    const errorMessage = errorData.message || 'Erro ao processar requisição';
    console.error('Asaas API error:', errorData);
    throw new Error(errorMessage);
  }
  // Lida com resposta vazia (204 ou corpo vazio)
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
}

// Create a CSP nonce for secure inline scripts (for CSP)
function generateNonce() {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Service for interacting with the Asaas payment gateway API using secure practices
 */
/**
 * Serviço para integração com o gateway Asaas.
 * Suporta pagamentos avulsos (createPayment) e assinaturas recorrentes (createSubscription).
 * Escolha o método conforme o plano contratado pelo cliente.
 */
export const asaasService = {

  /**
   * Cria uma assinatura (subscription) com cartão de crédito via proxy backend
   * @param subscriptionData Dados da assinatura
   */
  createSubscription: async (subscriptionData: AsaasSubscriptionRequest) => {
    try {
      const response = await apiRequest('/api/asaas/subscriptions', 'POST', subscriptionData);
      return response;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Erro ao criar assinatura";
      toast.error(message);
      throw error;
    }
  },

  /**
   * Create a new customer in Asaas
   * @param customerData Customer information
   * @returns Customer creation response
   */
  createCustomer: async (customerData: AsaasCustomer) => {
    try {
      const response = await apiRequest('/api/asaas/customers', 'POST', customerData);
      return response;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Erro ao criar cliente no Asaas";
      toast.error(message);
      throw error;
    }
  },

  /**
   * Create a new payment in Asaas with enhanced security
   * @param paymentData Payment information
   * @returns Payment creation response
   */
  createPayment: async (paymentData: AsaasPaymentRequest) => {
    try {
      // Sanitize credit card data if present
      let sanitizedPaymentData = { ...paymentData };
      
      if (paymentData.creditCard) {
        sanitizedPaymentData.creditCard = {
          holderName: paymentData.creditCard.holderName.trim(),
          number: paymentData.creditCard.number.replace(/\s+/g, ''),
          expiryMonth: paymentData.creditCard.expiryMonth.padStart(2, '0'),
          expiryYear: paymentData.creditCard.expiryYear.padStart(2, '0'),
          ccv: paymentData.creditCard.ccv
        };
      }

      const response = await apiRequest('/api/asaas/payments', 'POST', sanitizedPaymentData);
      return response;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Erro ao processar pagamento";
      toast.error(message);
      throw error;
    }
  },

  /**
   * Get payment information from Asaas
   * @param paymentId Payment ID
   * @returns Payment information
   */
  getPaymentInfo: async (paymentId: string) => {
    try {
      // PCI DSS compliant headers
      const headers = {
        'Content-Type': 'application/json',
        'access_token': ASAAS_API_KEY,
        'Cache-Control': 'no-store, no-cache'
      };

      const response = await fetch(`${ASAAS_API_URL}/payments/${paymentId}`, {
        method: 'GET',
        headers,
        credentials: 'same-origin'
      });

      return handleResponse(response);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Erro ao obter informações do pagamento";
      toast.error(message);
      throw error;
    }
  },

  /**
   * Create a new Asaas Checkout session
   * @param checkoutData Checkout information
   * @returns Checkout creation response with checkout URL
   */
  createCheckout: async (checkoutData: AsaasCheckoutRequest): Promise<AsaasCheckoutResponse> => {
    try {
      // In production, this should go through a backend proxy
      const response = await apiRequest('/api/asaas/checkouts', 'POST', checkoutData);

      // Mapear o campo 'link' para 'checkoutUrl' para manter compatibilidade com a interface
      return {
        ...response,
        checkoutUrl: response.checkoutUrl || response.link
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : "Erro ao criar checkout";
      if (!message.includes('Erro ao criar checkout:')) {
        toast.error(message);
      }
      throw error;
    }
  }
};
